import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { isPlatformPermission } from '../utils/adminMode';

/**
 * Dual-world permissions under multi-tenancy:
 * - canPlatform → userInfo.platformPermissions only (SaaS screens)
 * - canTenant / checkPermission → userInfo.tenantPermissions only (CMS screens)
 * Never OR the two worlds. Platform Admin ≠ tenant CMS Admin.
 */

const asStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((x) => (typeof x === 'string' ? x : (x as { name?: string })?.name))
    .filter(Boolean) as string[];
};

const useCheckPermission = () => {
  const user = useSelector((state: any) => state.user.userInfo);
  const multiTenancyEnabled = !!useSelector(
    (state: any) => state.app.featureFlags?.multiTenancyEnabled
  );
  const navigate = useNavigate();

  const platformPermissions = asStringList(
    user?.platformPermissions ?? user?.PlatformPermissions
  );
  const tenantPermissions = asStringList(
    user?.tenantPermissions ?? user?.TenantPermissions
  );
  /** Legacy authority permissions — single-tenant only. */
  const legacyPermissions = asStringList(user?.authorityInfo?.permissions);

  const effectiveTenantPermissions = multiTenancyEnabled
    ? tenantPermissions
    : tenantPermissions.length > 0
      ? tenantPermissions
      : legacyPermissions;

  const hasAnyFrom = (haystack: string[], permissions: string[]) => {
    if (!user) return false;
    if (permissions == null || permissions.length === 0) return true;
    return permissions.some((x) => haystack.includes(x));
  };

  const checkAllFrom = (haystack: string[], permissions: string[]) => {
    if (!user) return false;
    if (permissions == null || permissions.length === 0) return true;
    return permissions.every((x) => haystack.includes(x));
  };

  const isTenantAdmin = effectiveTenantPermissions.includes('Administrator');
  const isPlatformAdmin = platformPermissions.includes('Administrator');

  /** Any of the named platform SaaS permissions. */
  const canPlatform = (permissions: string[]) => {
    if (!user) return false;
    if (permissions == null || permissions.length === 0) return true;
    if (isPlatformAdmin) return true;
    return hasAnyFrom(platformPermissions, permissions);
  };

  const canAllPlatform = (permissions: string[]) => {
    if (!user) return false;
    if (permissions == null || permissions.length === 0) return true;
    if (isPlatformAdmin) return true;
    return checkAllFrom(platformPermissions, permissions);
  };

  /** Any of the named tenant CMS permissions (never platform names via Admin wildcard). */
  const canTenant = (permissions: string[]) => {
    if (!user) return false;
    if (permissions == null || permissions.length === 0) return true;
    if (hasAnyFrom(effectiveTenantPermissions, permissions)) return true;
    if (isTenantAdmin && permissions.every((p) => !isPlatformPermission(p))) {
      return true;
    }
    return false;
  };

  const canAllTenant = (permissions: string[]) => {
    if (!user) return false;
    if (permissions == null || permissions.length === 0) return true;
    if (checkAllFrom(effectiveTenantPermissions, permissions)) return true;
    if (isTenantAdmin && permissions.every((p) => !isPlatformPermission(p))) {
      return true;
    }
    return false;
  };

  const hasAnyPlatformPermission = canPlatform;
  const checkPlatformPermission = canAllPlatform;
  const hasAnyTenantPermission = canTenant;
  const checkTenantPermission = canAllTenant;

  /**
   * CMS screens only. Under multi-tenancy this never reads platformPermissions.
   * SaaS screens must use canPlatform.
   */
  const checkPermission = (permissions: string[]) => canAllTenant(permissions);
  const hasAnyPermission = (permissions: string[]) => canTenant(permissions);

  return {
    canPlatform,
    canTenant,
    canAllPlatform,
    canAllTenant,
    checkPermission,
    hasAnyPermission,
    checkPlatformPermission,
    hasAnyPlatformPermission,
    checkTenantPermission,
    hasAnyTenantPermission,
    navigate,
  };
};

export default useCheckPermission;
